import type { ConsentState } from './types.js';

/**
 * 소비 측 코드가 **동의 상태를 읽는 계약**.
 *
 * ## 왜 이게 핵심인가
 *
 * 요즘 추적기는 태그가 아니라 **번들 안에서 초기화**된다. 표식을 달 자리가
 * 없으므로 **사이트 코드가 동의 상태를 보고 호출을 미루는 것**이 유일한
 * 방법이다. 실제로 소비 제품의 분석·오류 추적 둘 다 그 모양이었고, 그래서
 * 표식 개수가 0 인데도 차단해야 할 것이 셋 있었다.
 *
 * ## 🔴 「언제 물어도 답이 나와야」 한다
 *
 * 소비 측 코드가 배너보다 **먼저 돌 수도, 나중에 돌 수도** 있다.
 *
 * | 먼저 물으면 | 나중에 물으면 |
 * |---|---|
 * | 아직 답이 없다 → **기다려야 한다** | 이미 정해졌다 → **즉시 알아야 한다** |
 *
 * 이벤트만 주면 **늦게 온 쪽이 이미 지나간 이벤트를 놓친다.** 상태만 주면
 * **먼저 온 쪽이 영원히 모른다.** 둘 다 필요하다.
 *
 * `subscribe` 는 **이미 정해져 있으면 그 자리에서 한 번 부르고**, 그 뒤
 * 바뀔 때마다 다시 부른다. 그래서 호출 순서를 신경 쓰지 않아도 된다.
 *
 * @example
 *   // 분석을 켜는 쪽 — 배너보다 먼저 돌든 나중에 돌든 같게 동작한다
 *   RayCookieBanner.subscribe((state) => {
 *     if (state.choices['analytics'] === 'granted') startAnalytics();
 *   });
 */

type Listener = (state: ConsentState) => void;

const listeners = new Set<Listener>();
let latest: ConsentState | null = null;

/** 배너가 선택을 확정할 때마다 부른다. */
export function publish(state: ConsentState): void {
  latest = state;
  for (const fn of [...listeners]) {
    try {
      fn(state);
    } catch {
      // **한 구독자가 터져도 나머지는 받는다.** 분석 초기화가 실패했다고
      // 오류 추적 게이팅까지 안 도는 상황을 만들지 않는다.
    }
  }
}

/** 지금 상태. 아직 안 정했으면 `null`. */
export function current(): ConsentState | null {
  return latest;
}

/**
 * 상태를 구독한다. **이미 정해져 있으면 즉시 한 번 부른다.**
 *
 * 끊는 함수를 돌려준다.
 */
export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  if (latest !== null) {
    // 늦게 붙은 구독자가 지나간 결정을 놓치지 않게 한다. 이것이 없으면
    // **배너가 먼저 끝난 경우 영원히 안 불린다.**
    try {
      fn(latest);
    } catch {
      /* 구독자 오류가 등록을 실패시키지 않는다 */
    }
  }
  return () => void listeners.delete(fn);
}

/**
 * 그 목적이 허용됐는지 **지금 답한다.**
 *
 * ⚠️ **아직 정해지지 않았으면 `false` 다.** 「모른다」와 「거부」를 같게
 * 다루는 것이고, 그래야 **결정 전에 켜지는 일이 없다.** 결정이 나면
 * 알아야 하는 코드는 `subscribe` 를 쓴다 — `allows` 만 한 번 보고 끝내면
 * **동의한 뒤에도 영영 안 켜진다.**
 */
export function allows(purposeId: string): boolean {
  return latest?.choices[purposeId] === 'granted';
}

/** 테스트용. 전역 상태를 되돌린다. */
export function reset(): void {
  listeners.clear();
  latest = null;
}
